import type { CardFilterChip } from './cardFilters'

/**
 * What a bulk action acts on (ADR-067 Decision 6, brief §23 item 9).
 *
 * Two scopes, and the distinction is the whole point of this type:
 *
 *   `ids`  — a genuine subset the reader picked card by card. These ids are
 *            real: every one of them names a card the reader tapped.
 *   `deck` — "everything", optionally narrowed by a filter chip and minus what
 *            the reader deselected. It holds NO card ids, because it cannot
 *            honestly hold them: the client has paged in 30 cards of a 93-card
 *            deck and any list it produced would be a claim about the deck it
 *            is in no position to make. The server resolves the membership.
 *
 * That is the defect this replaces. `Select all` used to collect the loaded
 * pages' ids while the counter printed that number as the deck's, so Delete and
 * Move destroyed or relocated a subset and the counter confirmed the reader's
 * belief that they had acted on everything. The same deck could report `30` on
 * one visit and `90` on the next depending on cache state the reader can
 * neither see nor control.
 *
 * An exclusion is allowed in `deck` scope for the same reason a relative move
 * is allowed on a partially loaded deck: "not this one" is true or false on its
 * own and says nothing about the rest of the deck, so it survives partial
 * loading intact.
 */
export type DeckSelection =
  | { scope: 'ids'; ids: Set<string> }
  | { scope: 'deck'; filter: CardFilterChip; excluded: Set<string> }

/** Deck-wide counts from `GET /decks/{id}/cards/counts` — the only honest
 * source for a deck-scoped selection's size. */
export interface DeckCardCounts {
  total: number
  no_translation: number
  no_example: number
  not_filled: number
}

/** The wire form of a selection — `CardSelection` on the backend. */
export type CardSelectionRequest =
  | { mode: 'ids'; card_ids: string[] }
  | { mode: 'deck'; filter: CardFilterChip; exclude_card_ids: string[] }

export const EMPTY_SELECTION: DeckSelection = { scope: 'ids', ids: new Set() }

export function selectWholeDeck(filter: CardFilterChip): DeckSelection {
  return { scope: 'deck', filter, excluded: new Set() }
}

export function isCardSelected(selection: DeckSelection, cardId: string): boolean {
  return selection.scope === 'ids'
    ? selection.ids.has(cardId)
    : !selection.excluded.has(cardId)
}

/**
 * Flip one card.
 *
 * In `deck` scope this adds or removes an exclusion rather than collapsing the
 * selection into a list: deselecting one card of "everything" still means
 * everything-but-that, which is a thing the server can resolve. Collapsing here
 * would silently hand back the loaded-set defect at the first tap.
 */
export function toggleCardSelected(selection: DeckSelection, cardId: string): DeckSelection {
  if (selection.scope === 'ids') {
    const ids = new Set(selection.ids)
    if (ids.has(cardId)) ids.delete(cardId)
    else ids.add(cardId)
    return { scope: 'ids', ids }
  }
  const excluded = new Set(selection.excluded)
  if (excluded.has(cardId)) excluded.delete(cardId)
  else excluded.add(cardId)
  return { ...selection, excluded }
}

export function setCardSelected(
  selection: DeckSelection, cardId: string, selected: boolean,
): DeckSelection {
  if (isCardSelected(selection, cardId) === selected) return selection
  return toggleCardSelected(selection, cardId)
}

/** Add several cards at once — the desktop shift-click range. Only meaningful
 * in `ids` scope; in `deck` scope they are already selected. */
export function addCardsSelected(selection: DeckSelection, cardIds: string[]): DeckSelection {
  if (selection.scope !== 'ids') {
    const excluded = new Set(selection.excluded)
    cardIds.forEach((id) => excluded.delete(id))
    return { ...selection, excluded }
  }
  const ids = new Set(selection.ids)
  cardIds.forEach((id) => ids.add(id))
  return { scope: 'ids', ids }
}

function countForChip(counts: DeckCardCounts, chip: CardFilterChip): number {
  switch (chip) {
    case 'all': return counts.total
    case 'no-translation': return counts.no_translation
    case 'no-example': return counts.no_example
    case 'not-filled': return counts.not_filled
  }
}

/**
 * How many cards this selection names — or `null` when that is not yet known.
 *
 * `null` is deliberately not 0 and not a loaded-set fallback. A deck-scoped
 * selection whose counts have not arrived has an unknown size, and every
 * caller must say "unknown" rather than print a number that would be wrong.
 * Substituting the loaded count here is precisely the defect being removed.
 *
 * The subtraction of exclusions is exact rather than approximate: an exclusion
 * can only be created by deselecting a card the feed rendered, and the feed
 * renders the active filter's members, so every excluded id was counted in
 * `countForChip`. `selectionFilterChanged` keeps that invariant true when the
 * chip moves under an active selection.
 */
export function selectionCount(
  selection: DeckSelection, counts: DeckCardCounts | undefined,
): number | null {
  if (selection.scope === 'ids') return selection.ids.size
  if (!counts) return null
  return Math.max(0, countForChip(counts, selection.filter) - selection.excluded.size)
}

/**
 * Whether select mode is on.
 *
 * An unknown count counts as active: the reader tapped `Select all`, and the
 * mode must not blink off while the count is in flight.
 */
export function isSelectionActive(
  selection: DeckSelection, counts: DeckCardCounts | undefined,
): boolean {
  const n = selectionCount(selection, counts)
  return n === null || n > 0
}

/** Rebase a deck-scoped selection onto a new filter. The old exclusions were
 * about a different set of cards, so keeping them would make the arithmetic in
 * `selectionCount` a guess. */
export function selectionFilterChanged(
  selection: DeckSelection, filter: CardFilterChip,
): DeckSelection {
  if (selection.scope !== 'deck' || selection.filter === filter) return selection
  return { scope: 'deck', filter, excluded: new Set() }
}

export function toSelectionRequest(selection: DeckSelection): CardSelectionRequest {
  return selection.scope === 'ids'
    ? { mode: 'ids', card_ids: Array.from(selection.ids) }
    : {
        mode: 'deck',
        filter: selection.filter,
        exclude_card_ids: Array.from(selection.excluded),
      }
}

/**
 * The ids a selection names, when the caller genuinely holds them.
 *
 * Returns `null` for a deck-scoped selection — not an approximation of it.
 * Callers that need a list must ask the server (`POST .../cards/selection`
 * with `include_ids`); a caller that quietly fell back to what it had loaded
 * would be the defect again, one layer down.
 */
export function localSelectionIds(selection: DeckSelection): string[] | null {
  return selection.scope === 'ids' ? Array.from(selection.ids) : null
}
